/**
 * components/modal.tsx — the design's `.t-modal` (backdrop + card + head/body/
 * foot). Sizes sm (400) / md (520) / wide (760). Backdrop press closes; the
 * card swallows the press so clicks inside don't dismiss.
 */
import React from 'react';
import { Modal as RNModal, View, Pressable, ScrollView, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, Radii as R, Shadows as SH, FontSize as FS, FontWeight as FW } from '../theme/theme';
import { Txt } from './ui';
import Button from './button';

export type ModalSize = 'sm' | 'md' | 'wide';

const WIDTH: Record<ModalSize, number> = { sm: 400, md: 520, wide: 760 };

export interface AppModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Remove body padding (e.g. preset preview renders its own rows). */
  flushBody?: boolean;
  bodyStyle?: StyleProp<ViewStyle>;
}

const AppModal: React.FC<AppModalProps> = ({
  open,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  flushBody = false,
  bodyStyle,
}) => (
  <RNModal visible={open} transparent animationType="fade" onRequestClose={onClose}>
    <Pressable
      onPress={onClose}
      style={{ flex: 1, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <Pressable
        // Swallow the press so it doesn't bubble to the backdrop.
        onPress={() => {}}
        style={[
          {
            width: WIDTH[size],
            maxWidth: '100%',
            maxHeight: '90%',
            backgroundColor: C.card,
            borderRadius: R.r4,
            overflow: 'hidden',
          },
          SH.lg as ViewStyle,
        ]}
      >
        {title !== undefined && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              paddingVertical: 14,
              paddingHorizontal: 18,
              borderBottomWidth: 1,
              borderBottomColor: C.line,
            }}
          >
            <Txt size={FS.lg} weight={FW.semibold} color={C.ink}>
              {title}
            </Txt>
            <Button variant="ghost" size="sm" iconOnly icon="x" onPress={onClose} />
          </View>
        )}
        <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={[!flushBody && { padding: 18 }, bodyStyle]}>
          {children}
        </ScrollView>
        {footer && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              paddingVertical: 12,
              paddingHorizontal: 18,
              borderTopWidth: 1,
              borderTopColor: C.line,
              backgroundColor: C.paper2,
            }}
          >
            {footer}
          </View>
        )}
      </Pressable>
    </Pressable>
  </RNModal>
);

export default AppModal;
